import React, { useEffect, useMemo, useRef } from "react";
import type { Key, Dispatch, SetStateAction } from "react";
import { Spin, Tree, TreeProps } from "antd";
import type { DataNode } from "antd/es/tree";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";
import type { Moment } from "moment";
import { useAppSelector } from "../../..";
import type { DictionaryDto } from "../../../lib/interfaces";
import { checkIsLoading } from "../../../lib/utils";
import type { EmptyFunction } from "../../../lib/types";
import type { IOfferListSearch } from "../../../store/slices/offering/types";
import "./OfferCatalog.less";

type ChildrenTreeData = { [key: number]: DataNode[] };

interface ComponentProps {
  isCatalogOpen: boolean;
  toggleCatalogOpen: EmptyFunction;
  catalogExpandedKeys: string[];
  setCatalogExpandedKeys: Dispatch<SetStateAction<string[]>>;
  onChangeSearchValue: (field: Partial<IOfferListSearch<Moment>>) => void;
  searchListValues: IOfferListSearch<Moment>;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferCatalog = (props: ComponentProps) => {
  const {
    isCatalogOpen,
    toggleCatalogOpen,
    catalogExpandedKeys,
    setCatalogExpandedKeys,
    onChangeSearchValue,
    searchListValues,
  } = props;
  const products = (useAppSelector(
    (state) => state.rtkDictionaries.products
  ) as unknown) as DictionaryDto[];
  const { serviceTypeList, serviceTypeListLoading } = useAppSelector((state) => state.offering);
  const { productsLoading } = useAppSelector((state) => state.rtkDictionaries);

  const childrenTreeData = useMemo((): ChildrenTreeData => {
    const result: ChildrenTreeData = {};
    [...serviceTypeList]
      .sort((a, b) => a.name.localeCompare(b.name, "ru"))
      .forEach(({ productId, name, id }, idx) => {
        const child = {
          title: name,
          key: `serviceTypeId:${id}`,
        };
        if (result[productId]) {
          result[productId].push(child);
        } else {
          result[productId] = [child];
        }
      });
    return result;
  }, [serviceTypeList]);

  const treeData = useMemo(
    (): DataNode[] =>
      [...products]
        .sort((a, b) => a.name.localeCompare(b.name, "ru"))
        .map(({ id, name }, idx) => ({
          title: name,
          key: `productId:${id}`,
          children: childrenTreeData[id],
        })),

    [products, childrenTreeData]
  );

  const onSelect: TreeProps["onSelect"] = (_, { node }) => {
    const [propName, value] = String(node.key).split(":");
    onChangeSearchValue({
      serviceTypeId: undefined,
      productId: undefined,
      [propName]: Number(value),
    });
  };

  const selectedKeys = useMemo(() => {
    const { productId, serviceTypeId } = searchListValues;
    if (productId !== undefined) {
      return [`productId:${productId}`];
    }
    if (serviceTypeId !== undefined) {
      return [`serviceTypeId:${serviceTypeId}`];
    }
    return [];
  }, [searchListValues]);

  const divTitleRef = useRef<HTMLDivElement>(null);

  const resizeHandler = () => {
    const { offsetHeight } = divTitleRef.current || {};
    const divTreeElements: NodeListOf<HTMLDivElement> | null = document?.querySelectorAll(
      ".ant-tree"
    );
    if (divTreeElements?.length > 0) {
      divTreeElements.forEach((el) => {
        el.style.height = `calc(100% - ${offsetHeight || 0}px)`;
      });
    }
  };

  useEffect(() => {
    window.addEventListener("resize", resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [divTitleRef.current]);

  return (
    <Spin
      spinning={checkIsLoading(productsLoading, serviceTypeListLoading)}
      style={{ height: "100%" }}
    >
      <div className="offer-catalog">
        {isCatalogOpen && (
          <div className="offer-catalog-opened">
            <div ref={divTitleRef} className="offer-catalog-opened__title">
              <h3>Каталог типов продукта</h3>
              <LeftOutlined onClick={toggleCatalogOpen} />
            </div>
            <Tree
              selectedKeys={selectedKeys}
              showLine={{ showLeafIcon: false }}
              treeData={treeData}
              showIcon={false}
              onSelect={onSelect}
              onExpand={(keys) => setCatalogExpandedKeys(keys.map(String))}
              expandedKeys={catalogExpandedKeys}
            />
          </div>
        )}

        {!isCatalogOpen && (
          <div className="offer-catalog-closed">
            <div>
              <RightOutlined onClick={toggleCatalogOpen} />
            </div>
            <div className="offer-catalog-closed__title">
              <h3>Каталог типов продукта</h3>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};
